import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ApartmentOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  SaveOutlined,
  SettingOutlined,
  TeamOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Input, Modal, Select, Spin, Switch, Tabs, Tag } from 'antd';
import rawCountries from '../../../../../assets/countries.json';
import { ClientsService } from '../services/clients.service';
import { UtilService } from '../../../../../core/services/util.service';
import { useProfile } from '../../profile/components/ProfileContext';
import { Client, ClientId } from '../models/clients.model';
import StagesTable from './Stages';
import { route } from '../../../../../constants/routes';
import './Company.css';
import './UserProfile.css';
import './ClientProfile.css';

interface CountryOption {
  value: string;
  name: string;
  flag: string;
  dialCode: string;
  label: JSX.Element;
}

const emptyClient = () => new ClientId();

const clientInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'CL';

const websiteUrl = (website: string) => {
  const trimmed = website?.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const ClientProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { companyId, isAdmin } = useProfile();
  const isEditing = Boolean(id);
  const isDeletedFromList = Boolean(location.state?.deleted);
  const requestedTab = id && location.state?.activeTab === '2' ? '2' : '1';
  const [clientId, setClientId] = useState<string | null>(id || null);
  const [client, setClient] = useState<ClientId>(emptyClient);
  const [initialClientState, setInitialClientState] =
    useState<ClientId>(emptyClient);
  const [activeTab, setActiveTab] = useState(requestedTab);
  const [selectedCountryCode, setSelectedCountryCode] = useState('CR');
  const [currencyOptions, setCurrencyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>(
    {}
  );
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const countryOptions = useMemo<CountryOption[]>(
    () =>
      rawCountries.map((country) => ({
        value: country.code,
        name: country.name,
        flag: country.flag,
        dialCode: country.dial_code,
        label: (
          <span className="user-profile__country-option">
            <img src={country.flag} alt="" />
            <span>
              {country.name} (+{country.dial_code})
            </span>
          </span>
        ),
      })),
    []
  );

  const selectedCountry =
    countryOptions.find((option) => option.value === selectedCountryCode) ||
    countryOptions.find((option) => option.value === 'CR') ||
    countryOptions[0];
  const readonly = isDeletedFromList || client.deleted;
  const canEdit = isAdmin && !readonly;
  const hasChanges =
    JSON.stringify(client) !== JSON.stringify(initialClientState);

  const phoneLocalValue = useMemo(() => {
    if (!client.primaryPhoneNumber) return '';
    const normalized = client.primaryPhoneNumber.replace(/\s+/g, '');
    const prefix = `+${selectedCountry?.dialCode || ''}`;
    return normalized.startsWith(prefix)
      ? normalized.slice(prefix.length)
      : normalized.replace(/^\+/, '');
  }, [client.primaryPhoneNumber, selectedCountry?.dialCode]);

  useEffect(() => {
    let active = true;
    const loadCurrencies = async () => {
      try {
        const result = await ClientsService.GetCurrency();
        if (!active) return;
        const options = Object.entries(result || {}).map(([code, symbol]) => ({
          label: String(symbol),
          value: code,
        }));
        setCurrencyOptions(options);
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };
    void loadCurrencies();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!companyId || !clientId) return;
    let active = true;
    const loadClient = async () => {
      setLoading(true);
      try {
        const result = await ClientsService.GetById(companyId, clientId);
        if (!active || !result) return;
        setClient(result);
        setInitialClientState(result);
        const matchedCountry = countryOptions.find((option) =>
          result.primaryPhoneNumber?.startsWith(`+${option.dialCode}`)
        );
        if (matchedCountry) setSelectedCountryCode(matchedCountry.value);
      } catch (error) {
        console.error('Error fetching client:', error);
        UtilService.Alert(
          'Client unavailable',
          'error',
          'The client profile could not be loaded. Please try again.'
        );
      } finally {
        if (active) setLoading(false);
      }
    };
    void loadClient();
    return () => {
      active = false;
    };
  }, [clientId, companyId, countryOptions]);

  const clearFieldError = (field: string) => {
    if (!validationErrors.includes(field)) return;
    setValidationErrors((errors) => errors.filter((error) => error !== field));
    setErrorMessages((messages) => {
      const next = { ...messages };
      delete next[field];
      return next;
    });
  };

  const updateField = (
    field: 'name' | 'website' | 'primaryContact' | 'primaryEmail',
    value: string
  ) => {
    setClient((current) => ({ ...current, [field]: value }));
    clearFieldError(field);
  };

  const updateCountry = (countryCode: string) => {
    const nextCountry = countryOptions.find(
      (option) => option.value === countryCode
    );
    if (!nextCountry) return;
    setSelectedCountryCode(countryCode);
    const localNumber = phoneLocalValue.replace(/\D/g, '');
    setClient((current) => ({
      ...current,
      primaryPhoneNumber: localNumber
        ? `+${nextCountry.dialCode}${localNumber}`
        : '',
    }));
  };

  const updatePhone = (value: string) => {
    const localNumber = value.replace(/\D/g, '');
    setClient((current) => ({
      ...current,
      primaryPhoneNumber: localNumber
        ? `+${selectedCountry?.dialCode || '506'}${localNumber}`
        : '',
    }));
    clearFieldError('primaryPhoneNumber');
  };

  const validateClient = (candidate: ClientId) => {
    const errors: string[] = [];
    const messages: Record<string, string> = {};

    if (!candidate.name?.trim()) {
      errors.push('name');
      messages.name = 'Client name is required';
    }
    if (!candidate.website?.trim()) {
      errors.push('website');
      messages.website = 'Website is required';
    } else {
      try {
        const url = new URL(websiteUrl(candidate.website));
        if (!url.hostname) throw new Error('Invalid hostname');
      } catch {
        errors.push('website');
        messages.website = 'Enter a valid website';
      }
    }
    if (!candidate.primaryContact?.trim()) {
      errors.push('primaryContact');
      messages.primaryContact = 'Contact name is required';
    }
    if (!candidate.primaryEmail?.trim()) {
      errors.push('primaryEmail');
      messages.primaryEmail = 'Contact email is required';
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate.primaryEmail.trim())
    ) {
      errors.push('primaryEmail');
      messages.primaryEmail = 'Enter a valid email address';
    }
    if (!candidate.primaryPhoneNumber?.trim()) {
      errors.push('primaryPhoneNumber');
      messages.primaryPhoneNumber = 'Phone number is required';
    } else if (!/^\+\d{7,15}$/.test(candidate.primaryPhoneNumber.trim())) {
      errors.push('primaryPhoneNumber');
      messages.primaryPhoneNumber = 'Enter a valid phone number';
    }
    if (!candidate.currency?.trim()) {
      errors.push('currency');
      messages.currency = 'Currency is required';
    }

    setValidationErrors(errors);
    setErrorMessages(messages);
    return errors.length === 0;
  };

  const saveClient = async () => {
    if (!validateClient(client) || !companyId) return;
    setSaving(true);
    const clientData = new Client(
      client.name.trim(),
      client.website.trim(),
      client.active,
      client.primaryContact.trim(),
      client.primaryEmail.trim(),
      client.primaryPhoneNumber.trim(),
      client.currency
    );

    try {
      if (clientId) {
        await ClientsService.Patch(companyId, clientData, clientId);
        const saved = { ...client, ...clientData, id: clientId };
        setClient(saved);
        setInitialClientState(saved);
        UtilService.Alert(
          'Client updated',
          'success',
          'The account and contact information were saved.'
        );
      } else {
        const response = await ClientsService.Post(companyId, clientData);
        setClient(response);
        setInitialClientState(response);
        setClientId(response.id);
        UtilService.Alert(
          'Client created',
          'success',
          'The client account is ready. You can now configure its stages.'
        );
        navigate(route(`/client-profile/${response.id}`), { replace: true });
      }
    } catch (error) {
      console.error('Error saving client:', error);
      UtilService.Alert(
        'Save unavailable',
        'error',
        'The client could not be saved. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setClient(initialClientState);
    setValidationErrors([]);
    setErrorMessages({});
    const matchedCountry = countryOptions.find((option) =>
      initialClientState.primaryPhoneNumber?.startsWith(`+${option.dialCode}`)
    );
    if (matchedCountry) setSelectedCountryCode(matchedCountry.value);
  };

  const goBack = () => {
    const leave = () =>
      navigate(route('/company/clients'), {
        state: { clientName: client.name },
      });
    if (!hasChanges) {
      leave();
      return;
    }
    Modal.confirm({
      title: 'Leave without saving?',
      content: 'Your changes to this client profile will be lost.',
      okText: 'Leave and discard',
      cancelText: 'Keep editing',
      okButtonProps: { danger: true },
      onOk: leave,
    });
  };

  const changeTab = (key: string) => {
    const select = () => setActiveTab(key);
    if (key === '2' && hasChanges) {
      Modal.confirm({
        title: 'Open stages without saving?',
        content:
          'Save or discard the client changes before configuring stages.',
        okText: 'Discard and open stages',
        cancelText: 'Keep editing',
        okButtonProps: { danger: true },
        onOk: () => {
          resetForm();
          select();
        },
      });
      return;
    }
    select();
  };

  if (loading) {
    return (
      <main
        className="user-profile-premium user-profile-premium--loading"
        aria-label="Loading client profile"
        aria-busy="true"
      >
        <Spin size="large" />
        <span>Loading client profile…</span>
      </main>
    );
  }

  const profileContent = (
    <div className="user-profile__layout">
      <div className="user-profile__content">
        <section className="user-profile__card">
          <div className="user-profile__card-header">
            <span className="user-profile__section-icon">
              <BankOutlined aria-hidden />
            </span>
            <div>
              <h2>Account identity</h2>
              <p>The client information used across jobs and applications.</p>
            </div>
          </div>

          <div className="client-profile__identity">
            <span className="client-profile__logo" aria-hidden>
              {clientInitials(client.name)}
            </span>
            <div>
              <strong>{client.name || 'New client'}</strong>
              <span>{client.website || 'Website not configured'}</span>
              <small>{clientId ? 'Client account' : 'New account setup'}</small>
            </div>
          </div>

          <div className="user-profile__fields client-profile__account-fields">
            <label className="user-profile__field">
              <span>Client name</span>
              <Input
                autoFocus={!clientId}
                placeholder="Client name"
                prefix={<ApartmentOutlined aria-hidden />}
                value={client.name}
                status={validationErrors.includes('name') ? 'error' : ''}
                disabled={!canEdit}
                onChange={(event) => updateField('name', event.target.value)}
              />
              {validationErrors.includes('name') ? (
                <small>{errorMessages.name}</small>
              ) : null}
            </label>

            <label className="user-profile__field">
              <span>Website</span>
              <div className="user-profile__field-action">
                <Input
                  placeholder="https://company.com"
                  prefix={<GlobalOutlined aria-hidden />}
                  value={client.website}
                  status={validationErrors.includes('website') ? 'error' : ''}
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField('website', event.target.value)
                  }
                />
                {client.website ? (
                  <a
                    href={websiteUrl(client.website)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open client website"
                  >
                    <GlobalOutlined aria-hidden />
                  </a>
                ) : null}
              </div>
              {validationErrors.includes('website') ? (
                <small>{errorMessages.website}</small>
              ) : null}
            </label>

            <label className="user-profile__field">
              <span>Default currency</span>
              <Select
                aria-label="Default currency"
                options={currencyOptions}
                placeholder="Select currency"
                value={client.currency || undefined}
                status={validationErrors.includes('currency') ? 'error' : ''}
                disabled={!canEdit}
                onChange={(value) => {
                  setClient((current) => ({ ...current, currency: value }));
                  clearFieldError('currency');
                }}
              />
              {validationErrors.includes('currency') ? (
                <small>{errorMessages.currency}</small>
              ) : (
                <em>Used as the default when creating jobs for this client.</em>
              )}
            </label>
          </div>
        </section>

        <section className="user-profile__card">
          <div className="user-profile__card-header">
            <span className="user-profile__section-icon">
              <IdcardOutlined aria-hidden />
            </span>
            <div>
              <h2>Primary contact</h2>
              <p>The person recruiters contact about hiring activity.</p>
            </div>
          </div>

          <div className="user-profile__fields">
            <label className="user-profile__field">
              <span>Contact name</span>
              <Input
                placeholder="Full name"
                prefix={<TeamOutlined aria-hidden />}
                value={client.primaryContact}
                status={
                  validationErrors.includes('primaryContact') ? 'error' : ''
                }
                disabled={!canEdit}
                onChange={(event) =>
                  updateField('primaryContact', event.target.value)
                }
              />
              {validationErrors.includes('primaryContact') ? (
                <small>{errorMessages.primaryContact}</small>
              ) : null}
            </label>

            <label className="user-profile__field">
              <span>Contact email</span>
              <div className="user-profile__field-action">
                <Input
                  type="email"
                  placeholder="name@company.com"
                  prefix={<MailOutlined aria-hidden />}
                  value={client.primaryEmail}
                  status={
                    validationErrors.includes('primaryEmail') ? 'error' : ''
                  }
                  disabled={!canEdit}
                  onChange={(event) =>
                    updateField('primaryEmail', event.target.value)
                  }
                />
                {client.primaryEmail ? (
                  <a
                    href={`mailto:${client.primaryEmail}`}
                    aria-label="Email primary contact"
                  >
                    <MailOutlined aria-hidden />
                  </a>
                ) : null}
              </div>
              {validationErrors.includes('primaryEmail') ? (
                <small>{errorMessages.primaryEmail}</small>
              ) : null}
            </label>

            <label className="user-profile__field">
              <span>Phone number</span>
              <div className="user-profile__phone">
                <Select
                  aria-label="Country calling code"
                  options={countryOptions}
                  optionLabelProp="label"
                  value={selectedCountryCode}
                  disabled={!canEdit}
                  onChange={updateCountry}
                />
                <Input
                  type="tel"
                  placeholder="Phone number"
                  prefix={<PhoneOutlined aria-hidden />}
                  value={phoneLocalValue}
                  status={
                    validationErrors.includes('primaryPhoneNumber')
                      ? 'error'
                      : ''
                  }
                  disabled={!canEdit}
                  onChange={(event) => updatePhone(event.target.value)}
                />
                {client.primaryPhoneNumber ? (
                  <a
                    href={`https://wa.me/${client.primaryPhoneNumber.replace(
                      /\D/g,
                      ''
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open WhatsApp"
                  >
                    <WhatsAppOutlined aria-hidden />
                  </a>
                ) : null}
              </div>
              {validationErrors.includes('primaryPhoneNumber') ? (
                <small>{errorMessages.primaryPhoneNumber}</small>
              ) : null}
            </label>
          </div>
        </section>
      </div>

      <aside className="user-profile__sidebar">
        <section className="user-profile__card">
          <div className="user-profile__card-header">
            <span className="user-profile__section-icon">
              <SettingOutlined aria-hidden />
            </span>
            <div>
              <h2>Account status</h2>
              <p>Control whether this client is available for new jobs.</p>
            </div>
          </div>
          <div className="user-profile__active-setting">
            <div>
              <strong>Active client</strong>
              <small>
                Inactive clients remain visible but should not receive new jobs.
              </small>
            </div>
            <Switch
              checked={client.active}
              disabled={!canEdit}
              onChange={(checked) =>
                setClient((current) => ({ ...current, active: checked }))
              }
            />
          </div>
        </section>

        <section className="user-profile__save-card">
          <div className="user-profile__save-state">
            {hasChanges ? (
              <>
                <span className="user-profile__save-dot" />
                Unsaved changes
              </>
            ) : (
              <>
                <CheckCircleOutlined aria-hidden />
                All changes saved
              </>
            )}
          </div>
          <dl>
            <div>
              <dt>Status</dt>
              <dd>{client.active ? 'Active' : 'Inactive'}</dd>
            </div>
            <div>
              <dt>Currency</dt>
              <dd>{client.currency || 'Not selected'}</dd>
            </div>
          </dl>
          <button
            className="company-primary-action"
            type="button"
            disabled={saving || !isAdmin || !hasChanges || readonly}
            onClick={() => void saveClient()}
          >
            <SaveOutlined aria-hidden />
            {saving ? 'Saving…' : clientId ? 'Update client' : 'Create client'}
          </button>
          {hasChanges && clientId ? (
            <button
              className="company-secondary-action"
              type="button"
              disabled={saving}
              onClick={resetForm}
            >
              Discard changes
            </button>
          ) : null}
        </section>
      </aside>
    </div>
  );

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <BankOutlined aria-hidden />
          Account details
        </span>
      ),
      children: profileContent,
    },
    ...(clientId
      ? [
          {
            key: '2',
            label: (
              <span>
                <SettingOutlined aria-hidden />
                Hiring stages
              </span>
            ),
            children: (
              <section className="client-profile__stages">
                <StagesTable clientName={client.name} />
              </section>
            ),
          },
        ]
      : []),
  ];

  return (
    <main className="user-profile-premium client-profile-premium">
      <header className="user-profile__header">
        <button className="user-profile__back" type="button" onClick={goBack}>
          <ArrowLeftOutlined aria-hidden />
          Clients
        </button>
        <div className="user-profile__heading">
          <div>
            <span>Client portfolio</span>
            <h1>{clientId ? 'Update client' : 'Create client'}</h1>
            <p>
              Manage account identity, the primary contact, and hiring stages.
            </p>
          </div>
          <Tag
            className={`user-profile__status user-profile__status--${
              readonly ? 'archived' : client.active ? 'active' : 'inactive'
            }`}
          >
            {readonly ? 'Archived' : client.active ? 'Active' : 'Inactive'}
          </Tag>
        </div>
      </header>

      <Tabs
        className="client-profile__tabs"
        activeKey={activeTab}
        items={tabItems}
        onChange={changeTab}
      />
    </main>
  );
};

export default ClientProfile;
